import { type Database } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import {
  type DatabaseChangeset,
  type DatabaseDraft,
  getDatabaseDraft
} from '@databases/utils/database/changesets';
import { applyDatabaseView } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { omit } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import type { DefaultRootState } from '../redux';

export const emptyArr = Object.freeze([]) as never[];

// Y.js is the source of truth for changes, so we always use an empty changeset
export const selectDraftedChangeset = (): DatabaseChangeset => emptyArr;

const selectDatabaseDraftByContentIdInternal = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    cacheKey?: 'main' | 'view';
    viewSettings?: DatabaseViewSettings;
    ignoredEntries?: string[];
  },
  Database,
  DatabaseViewSettings | undefined,
  string[] | undefined,
  DefaultRootState,
  DatabaseDraft
>(
  selectDatabaseByContentIdRequired,
  (state, { viewSettings }) => viewSettings,
  (state, { ignoredEntries }) => ignoredEntries,
  (state) => state,
  (database, viewSettings, ignoredEntries, state) => {
    // Y.js is the source of truth for changes, so we always use an empty changeset
    const databaseDraft = getDatabaseDraft(database, emptyArr);
    return viewSettings
      ? applyDatabaseView(database, databaseDraft, viewSettings, state, ignoredEntries)
      : databaseDraft;
  }
)(
  (state, { contentId, cacheKey = 'main', viewSettings }) =>
    `${contentId}-${cacheKey}-${!!viewSettings}`
);

export const selectDatabaseDraftByContentId = createCachedSelector<
  DefaultRootState,
  {
    contentId: string;
    cacheKey?: 'main' | 'view';
    /**
     * If true, deleted fields/entries will be present in the draft (marked as deleted in the "deletedFields" and
     * "deletedEntries" properties.
     * If false, deleted fields/entries will not be present in the draft.
     *
     * This property is not optional as it is a common source of error. Components that receive a fieldId/entryId
     * as a prop and use that to load the field/entry from the database should almost always set this to true,
     * as many parts of the UI keep showing deleted fields/entries until the draft is saved.
     * Setting this to false in combination with using getDatabaseFieldRequired/getDatabaseEntryRequired will usually
     * lead to exceptions and component crashes, so in such cases usually getDatabaseFieldOptional/getDatabaseEntryOptional
     * need to be used.
     */
    includeDeleted: boolean;
    viewSettings?: DatabaseViewSettings;
    ignoredEntries?: string[];
  },
  DatabaseDraft,
  boolean,
  DatabaseDraft
>(
  selectDatabaseDraftByContentIdInternal,
  (state, { includeDeleted }) => includeDeleted,
  (databaseDraft, includeDeleted) => {
    if (includeDeleted) {
      return databaseDraft;
    } else {
      return {
        ...databaseDraft,
        fields: databaseDraft.fields.filter(
          (fieldId) => !databaseDraft.deletedFields.includes(fieldId)
        ),
        fieldsById: omit(databaseDraft.fieldsById, databaseDraft.deletedFields),
        entries: databaseDraft.entries.filter(
          (entryId) => !databaseDraft.deletedEntries.includes(entryId)
        ),
        entriesById: omit(databaseDraft.entriesById, databaseDraft.deletedEntries)
      };
    }
  }
)(
  (state, { contentId, cacheKey = 'main', viewSettings, includeDeleted }) =>
    `${contentId}-${cacheKey}-${!!viewSettings}-${includeDeleted}`
);
